import type { ComponentProps } from "react";
import { lazy, Suspense, useRef } from "react";
import type { Props as ReactPhoneInputProps, Value } from "react-phone-number-input";
import "react-phone-number-input/style.css";

import { Input } from "../input";
import { Skeleton } from "../skeleton";
import { InputSkeleton } from "../skeletons";

import { assignRef } from "~/shared/react";
import type { Empty } from "~/shared/str";
import { EMPTY } from "~/shared/str";

const ReactPhoneInput = lazy(() => import("react-phone-number-input"));

const DefaultInputComponent = Input;
type DefaultInputComponentProps = Pick<ComponentProps<typeof DefaultInputComponent>, "ref">;
type NativeInput = HTMLInputElement;

type Props<InputComponentProps extends DefaultInputComponentProps> = Omit<
  ReactPhoneInputProps<InputComponentProps>,
  "value" | "onChange"
> & {
  value?: ReactPhoneInputProps<InputComponentProps>["value"] | undefined;
  onChange(value: Value | Empty): void;
};

function PhoneInput<
  InputComponentProps extends DefaultInputComponentProps = ComponentProps<
    typeof DefaultInputComponent
  >,
>({ ref, inputComponent = DefaultInputComponent, value, ...props }: Props<InputComponentProps>) {
  const { onChange, style, ...fallbackInputProps } = props;
  const inputRef = useRef<NativeInput>(null);

  return (
    <Suspense
      fallback={
        <div className="flex gap-1">
          <Skeleton className="size-9 rounded-md" />
          <InputSkeleton asChild>
            <input
              disabled
              autoComplete="tel"
              style={style as ComponentProps<"input">["style"]}
              className="w-full"
              {...fallbackInputProps}
            />
          </InputSkeleton>
        </div>
      }
    >
      <ReactPhoneInput
        ref={(instance) => {
          inputRef.current = instance as NativeInput | null;
          assignRef(ref, instance as NativeInput | null);
        }}
        inputComponent={inputComponent}
        value={value}
        {...props}
        onChange={
          ((newValue) => {
            onChange(newValue ?? EMPTY);

            const LENGTH = 2;
            if (!value && newValue && newValue.length === LENGTH && newValue.startsWith("+")) {
              setTimeout(() => {
                inputRef.current?.setSelectionRange(LENGTH, LENGTH);
              }, 0);
            }
          }) as ReactPhoneInputProps<InputComponentProps>["onChange"]
        }
      />
    </Suspense>
  );
}

export { PhoneInput };
